import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { formattedDate } from '../../../../utils/dateUtils';
import { publishNotification } from '../../../../utils/notifications/publish-notification';
import { useDialog } from '../../../hooks/use-dialog';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { DateTimePickerDialog } from '../../date-time-picker-dialog/date-time-picker-dialog';
import { Icon } from '../../icon/icon';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { TextBoxWithRightButton } from '../styles/styles';

const validToDateQueryKey = (projectId: string) => ['getProjectValidToDate', projectId];

const ValidToDateSection = () => {
  const { t } = useTranslation();
  const { project } = React.useContext(ProjectContext);
  const queryClient = useQueryClient();

  const [validToDate, setValidToDate] = React.useState<Date | null>(project.validToDate);
  const dateTimePickerDialog = useDialog();

  const { data: validToDateModel } = useQuery({
    queryKey: validToDateQueryKey(project.id),
    enabled: !!project.id,
    queryFn: async () => {
      const response = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceInternal()
        .getValidToDate({ path: { projectId: project.id } });
      return response.data;
    },
    networkMode: 'always',
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Sync the separately-loaded valid-to date into local state, which also
  // backs optimistic updates when the user changes or clears the date.
  React.useEffect(() => {
    if (validToDateModel) {
      setValidToDate(validToDateModel.validToDate ? new Date(validToDateModel.validToDate) : null);
    }
  }, [validToDateModel]);

  const updateProjectValidToDate = React.useCallback(
    async (date: Date | null) => {
      if (validToDate?.getTime() === date?.getTime()) {
        return;
      }

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
      await projectService.update({
        path: { id: project.id },
        body: { validToDate: date ? formattedDate(date) : null },
      });

      await queryClient.invalidateQueries({ queryKey: validToDateQueryKey(project.id) });

      publishNotification('succ0318');
    },
    [project.id, validToDate, queryClient],
  );

  return (
    <Component>
      <Stack spacing={0.5}>
        <LabelledContainer text={t('projectSettings.status.validToDate', { ns: 'components' })}>
          {(formElementId) => (
            <TextBoxWithRightButton>
              <TextBox
                id={formElementId()}
                readOnly={true}
                readOnlyCursor="pointer"
                value={validToDate ? formattedDate(validToDate) : ''}
                onClick={(event) => {
                  event.stopPropagation();
                  dateTimePickerDialog.show();
                }}
              />
              <Button
                aria-label="date-picker-button"
                disabled={!validToDate}
                title={t('projectSettings.status.removeDate', { ns: 'components' })}
                variant="contained"
                onClick={async () => {
                  setValidToDate(null);
                  await updateProjectValidToDate(null);
                }}
              >
                <Icon icon={['fas', 'xmark']} />
              </Button>
            </TextBoxWithRightButton>
          )}
        </LabelledContainer>

        <InfoBox color="yellow">
          <p>{t('projectSettings.status.validToDateHelp', { ns: 'components' })}</p>
          <p>{t('projectSettings.status.validToDateHelp2', { ns: 'components' })}</p>
          <p>{t('projectSettings.status.validToDateHelp3', { ns: 'components' })}</p>
        </InfoBox>
      </Stack>

      {dateTimePickerDialog.render(
        <DateTimePickerDialog
          displayTime={false}
          startDate={validToDate ?? new Date()}
          onCancel={() => {
            dateTimePickerDialog.hide();
          }}
          onDateChoosen={async (date) => {
            setValidToDate(date);
            dateTimePickerDialog.hide();
            await updateProjectValidToDate(date);
          }}
        />,
      )}
    </Component>
  );
};

const Component = styled.div``;

ValidToDateSection.styled = Component;

export { ValidToDateSection };
